/**
 * PlanningGrilleUnifiee — LA grille de planning unique, partagée par toutes les
 * briques (service technique, GRC, et à venir scolaire/PE/urba/compta).
 *
 * Extraite telle quelle du Service technique (qui avait la bonne mécanique :
 * grille horaire 7 jours + glisser-déposer + affichage agenda côte à côte des
 * chevauchements). Rendue générique :
 *   - `accent` : couleur du module (tech, grc, …) — pilote le survol de drop.
 *   - `afficherNumero` : n'affiche le numéro (ex. signalement ST) que si demandé.
 *
 * Un créneau posé ici = l'agent est présent sur ce créneau (présence + travail
 * dans la même grille). Il n'y a plus deux plannings.
 *
 * Le drag-and-drop est fourni par le `DndContext` du parent : cette grille
 * expose les zones droppables (HourDropZone, id `drop-<dayIdx>-<hour>`) et rend
 * les créneaux draggables (id `creneau-<id>`). Le parent gère onDragEnd.
 */
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { PlanningAccent } from '@/components/planning/PlanningPresence';

export const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
export const SLOT_PX = 44; // hauteur d'une heure en px

/** Forme minimale d'un créneau affichable dans la grille (commune à toutes les briques). */
export type CreneauGrille = {
  id: string;
  agentUserId: string;
  agentFullName: string;
  agentCouleurMarker: string | null;
  dateDebut: string;
  dateFin: string;
  titre: string;
  description: string | null;
  signalementId: string | null;
  signalementNumero: string | null;
  type: string;
  couleurMarker: string | null;
};

/** Classes de survol de drop par accent (listées en dur pour survivre au purge Tailwind). */
const DROP_ACCENT: Record<PlanningAccent, string> = {
  tech:          'border-tech bg-tech-tint/40',
  grc:           'border-grc bg-grc-tint/40',
  scolaire:      'border-scolaire bg-scolaire-tint/40',
  rh:            'border-rh bg-rh-tint/40',
  petite_enfance:'border-petite-enfance bg-petite-enfance-tint/40',
  urbanisme:     'border-urbanisme bg-urbanisme-tint/40',
  asso:          'border-asso bg-asso-tint/40',
  comptable:     'border-comptable bg-comptable-tint/40',
};

export function PlanningGrilleUnifiee({
  days,
  todayKey,
  creneaux,
  onCreneauClick,
  colorOfCreneau,
  nonTravailles,
  accent = 'tech',
  afficherNumero = false,
  filterAgentId,
}: {
  days: Array<{ label: string; date: Date }>;
  todayKey: string;
  creneaux: CreneauGrille[];
  onCreneauClick: (c: CreneauGrille) => void;
  colorOfCreneau: (c: CreneauGrille) => string;
  nonTravailles: Map<string, string>;
  accent?: PlanningAccent;
  afficherNumero?: boolean;
  /** Vue individuelle : si défini, seuls les créneaux de cet agent sont affichés. */
  filterAgentId?: string;
}) {
  const ymdLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const firstHour = HOURS[0]!;
  const gridHeight = HOURS.length * SLOT_PX;
  const nbJours = days.length;
  const dropClass = DROP_ACCENT[accent] ?? DROP_ACCENT.tech;
  const creneauxVisibles = filterAgentId ? creneaux.filter((c) => c.agentUserId === filterAgentId) : creneaux;

  return (
    <div className="rounded-r-xl border border-border bg-surface/55 backdrop-blur-glass p-2 shadow-soft overflow-x-auto">
      <div className="grid min-w-[640px]" style={{ gridTemplateColumns: `44px repeat(${nbJours}, 1fr)` }}>
        {/* En-tête */}
        <div className="border-b border-border" />
        {days.map((d, i) => {
          const isToday = d.date.toDateString() === todayKey;
          const libelleNT = nonTravailles.get(ymdLocal(d.date));
          const estVacances = libelleNT?.startsWith('Vacances');
          return (
            <div key={i} className="space-y-0.5 border-b border-border pb-1 text-center">
              <div className={`text-2xs uppercase tracking-r-eyebrow ${isToday ? 'font-semibold text-ink' : 'text-ink-subtle'}`}>
                {d.label} {d.date.getDate()}
              </div>
              {libelleNT && (
                <span className={['inline-block rounded-r-pill px-1.5 py-0.5 text-[9px] font-semibold',
                  estVacances ? 'bg-amber-400/25 text-amber-200' : 'bg-violet-400/25 text-violet-200'].join(' ')}>
                  {estVacances ? '🏖 ' + libelleNT.replace('Vacances ', '') : '⚑ Férié'}
                </span>
              )}
            </div>
          );
        })}

        {/* Colonne heures */}
        <div className="relative" style={{ height: gridHeight }}>
          {HOURS.map((h, i) => (
            <div key={h} className="absolute right-1 text-right text-2xs text-ink-dim" style={{ top: i * SLOT_PX - 6 }}>{h}h</div>
          ))}
        </div>

        {/* Colonnes jour (droppables) */}
        {days.map((d, dayIdx) => (
          <DayColumn
            key={dayIdx}
            dayIdx={dayIdx}
            date={d.date}
            height={gridHeight}
            firstHour={firstHour}
            dropClass={dropClass}
            afficherNumero={afficherNumero}
            creneaux={creneauxVisibles.filter((c) => {
              const cd = new Date(c.dateDebut);
              return cd.getFullYear() === d.date.getFullYear() && cd.getMonth() === d.date.getMonth() && cd.getDate() === d.date.getDate();
            })}
            onCreneauClick={onCreneauClick}
            colorOfCreneau={colorOfCreneau}
          />
        ))}
      </div>
    </div>
  );
}

function DayColumn({
  dayIdx, date: _date, height, firstHour, creneaux, onCreneauClick, colorOfCreneau, dropClass, afficherNumero,
}: {
  dayIdx: number;
  date: Date;
  height: number;
  firstHour: number;
  creneaux: CreneauGrille[];
  onCreneauClick: (c: CreneauGrille) => void;
  colorOfCreneau: (c: CreneauGrille) => string;
  dropClass: string;
  afficherNumero: boolean;
}) {
  // Positionnement côte à côte des créneaux qui se chevauchent (façon agenda).
  const sorted = creneaux.slice().sort((a, b) => new Date(a.dateDebut).getTime() - new Date(b.dateDebut).getTime());
  const layout = new Map<string, { col: number; cols: number }>();
  let cluster: CreneauGrille[] = [];
  const flush = () => {
    if (cluster.length === 0) return;
    const colsEnd: number[] = [];
    const assign = new Map<string, number>();
    for (const c of cluster) {
      const start = new Date(c.dateDebut).getTime();
      let placed = -1;
      for (let i = 0; i < colsEnd.length; i++) {
        if (start >= colsEnd[i]!) { placed = i; break; }
      }
      if (placed === -1) { placed = colsEnd.length; colsEnd.push(0); }
      colsEnd[placed] = new Date(c.dateFin).getTime();
      assign.set(c.id, placed);
    }
    const total = colsEnd.length;
    for (const c of cluster) layout.set(c.id, { col: assign.get(c.id)!, cols: total });
    cluster = [];
  };
  let clusterEnd = 0;
  for (const c of sorted) {
    const start = new Date(c.dateDebut).getTime();
    if (cluster.length > 0 && start >= clusterEnd) flush();
    cluster.push(c);
    clusterEnd = Math.max(clusterEnd, new Date(c.dateFin).getTime());
  }
  flush();

  return (
    <div className="relative border-l border-border/50" style={{ height }}>
      {HOURS.map((hour, i) => (
        <HourDropZone key={hour} dayIdx={dayIdx} hour={hour} top={i * SLOT_PX} dropClass={dropClass} />
      ))}
      {creneaux.map((c) => {
        const debut = new Date(c.dateDebut);
        const fin = new Date(c.dateFin);
        const startH = debut.getHours() + debut.getMinutes() / 60;
        const durH = Math.max(0.5, (fin.getTime() - debut.getTime()) / 3600000);
        const top = (startH - firstHour) * SLOT_PX;
        const h = durH * SLOT_PX;
        const lay = layout.get(c.id) ?? { col: 0, cols: 1 };
        return (
          <PositionedCreneau
            key={c.id}
            creneau={c}
            top={top}
            height={h}
            col={lay.col}
            cols={lay.cols}
            color={colorOfCreneau(c)}
            afficherNumero={afficherNumero}
            onClick={() => onCreneauClick(c)}
          />
        );
      })}
    </div>
  );
}

function HourDropZone({ dayIdx, hour, top, dropClass }: { dayIdx: number; hour: number; top: number; dropClass: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: `drop-${dayIdx}-${hour}`, data: { dayIdx, hour } });
  return (
    <div
      ref={setNodeRef}
      className={['absolute left-0 right-0 border-t border-dashed transition-colors', isOver ? dropClass : 'border-border/30'].join(' ')}
      style={{ top, height: SLOT_PX }}
    />
  );
}

function PositionedCreneau({
  creneau, top, height, col, cols, color, onClick, afficherNumero,
}: {
  creneau: CreneauGrille; top: number; height: number; col: number; cols: number; color: string; onClick: () => void; afficherNumero: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `creneau-${creneau.id}`,
    data: { type: 'creneau', payload: creneau },
  });
  const debut = new Date(creneau.dateDebut);
  const fin = new Date(creneau.dateFin);
  const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}h${d.getMinutes() ? String(d.getMinutes()).padStart(2, '0') : ''}`;

  const gapPct = 2;
  const widthPct = (100 - gapPct * (cols - 1)) / cols;
  const leftPct = col * (widthPct + gapPct);

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={(e) => { if (!isDragging) { e.stopPropagation(); onClick(); } }}
      {...listeners}
      {...attributes}
      className="absolute overflow-hidden rounded-r-md px-2 py-1 text-left text-white shadow-soft transition-opacity"
      style={{
        top: top + 1, height: Math.max(24, height - 2),
        left: `calc(${leftPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
        backgroundColor: color, borderLeft: `3px solid ${color}`,
        opacity: isDragging ? 0.5 : 1, filter: 'brightness(1.05)',
        outline: cols > 1 ? '1px solid rgba(255,255,255,0.35)' : undefined,
      }}
    >
      {afficherNumero && creneau.signalementNumero && <div className="text-[9px] font-bold opacity-90">{creneau.signalementNumero}</div>}
      <div className="truncate text-[11px] font-semibold leading-tight">{creneau.titre}</div>
      <div className="text-[10px] opacity-85">{cols > 1 ? '' : creneau.agentFullName.split(' ')[0] + ' · '}{hhmm(debut)}–{hhmm(fin)}</div>
    </button>
  );
}
